import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PolylineIcon from "@mui/icons-material/Polyline";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";

export default function ImageDrawingAnswer({
  imageUrl = "",
  value = "",
  onChange = () => {},
  disabled = false,
}) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const previewImageDataRef = useRef(null);

  const [drawMode, setDrawMode] = useState(false);
  const [shapeType, setShapeType] = useState("rectangle"); // rectangle | circle | polygon
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const [polygonPoints, setPolygonPoints] = useState([]);
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
  const [currentMousePos, setCurrentMousePos] = useState(null);

  const setCanvasSizeFromImage = () => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const prevWidth = canvas.width;
    const prevHeight = canvas.height;
    let existingDrawing = null;
    if (prevWidth > 0 && prevHeight > 0) {
      existingDrawing = document.createElement("canvas");
      existingDrawing.width = prevWidth;
      existingDrawing.height = prevHeight;
      existingDrawing.getContext("2d").drawImage(canvas, 0, 0);
    }
    canvas.width = img.clientWidth;
    canvas.height = img.clientHeight;
    if (existingDrawing) {
      canvas
        .getContext("2d")
        .drawImage(existingDrawing, 0, 0, canvas.width, canvas.height);
    }
  };

  const getMousePos = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    const clientY = event.touches?.[0]?.clientY ?? event.clientY;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const restoreCanvas = (dataUrl) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!dataUrl) return;
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = dataUrl;
  };

  const restorePreviewBase = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || !previewImageDataRef.current) return;
    ctx.putImageData(previewImageDataRef.current, 0, 0);
  };

  const drawPolygon = (ctx, points, currentPos, close = false) => {
    if (!points.length) return;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i += 1) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    if (currentPos && !close) {
      ctx.lineTo(currentPos.x, currentPos.y);
    }
    if (close && points.length > 2) {
      ctx.closePath();
    }
    ctx.stroke();

    points.forEach((p, index) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, index === 0 ? 5 : 4, 0, Math.PI * 2);
      ctx.fillStyle = "#00c853";
      ctx.fill();
    });
  };

  const emitMergedAnswer = () => {
    const canvas = canvasRef.current;
    const merged = document.createElement("canvas");
    if (!canvas) return;
    merged.width = canvas.width;
    merged.height = canvas.height;
    const mergedCtx = merged.getContext("2d");

    if (imageUrl) {
      const bg = new Image();
      bg.onload = () => {
        mergedCtx.drawImage(bg, 0, 0, merged.width, merged.height);
        mergedCtx.drawImage(canvas, 0, 0);
        onChange(merged.toDataURL("image/png"));
      };
      bg.src = imageUrl;
      return;
    }
    mergedCtx.drawImage(canvas, 0, 0);
    onChange(merged.toDataURL("image/png"));
  };

  const handleMouseDown = (event) => {
    if (!drawMode || disabled) return;
    const pos = getMousePos(event);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#ff1744";
    ctx.lineWidth = 2;

    if (shapeType === "polygon") {
      if (!isDrawingPolygon) {
        setIsDrawingPolygon(true);
        previewImageDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
        setPolygonPoints([pos]);
      } else {
        const firstPoint = polygonPoints[0];
        const distance = Math.sqrt(
          Math.pow(pos.x - firstPoint.x, 2) + Math.pow(pos.y - firstPoint.y, 2)
        );
        if (distance < 12 && polygonPoints.length > 2) {
          drawPolygon(ctx, polygonPoints, null, true);
          setPolygonPoints([]);
          setIsDrawingPolygon(false);
          setCurrentMousePos(null);
          emitMergedAnswer();
        } else {
          setPolygonPoints((prev) => [...prev, pos]);
        }
      }
      return;
    }

    setIsDrawing(true);
    setStartPos(pos);
    previewImageDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
  };

  const handleMouseMove = (event) => {
    if (!drawMode || disabled) return;
    const pos = getMousePos(event);

    if (shapeType === "polygon" && isDrawingPolygon) {
      setCurrentMousePos(pos);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      restorePreviewBase();
      ctx.strokeStyle = "#ff1744";
      ctx.lineWidth = 2;
      drawPolygon(ctx, polygonPoints, pos, false);
      return;
    }

    if (!isDrawing || !previewImageDataRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    restorePreviewBase();
    ctx.strokeStyle = "#ff1744";
    ctx.lineWidth = 2;
    ctx.beginPath();

    if (shapeType === "rectangle") {
      const x = Math.min(startPos.x, pos.x);
      const y = Math.min(startPos.y, pos.y);
      const width = Math.abs(pos.x - startPos.x);
      const height = Math.abs(pos.y - startPos.y);
      ctx.strokeRect(x, y, width, height);
    } else if (shapeType === "circle") {
      const radius = Math.sqrt(
        Math.pow(pos.x - startPos.x, 2) + Math.pow(pos.y - startPos.y, 2)
      );
      ctx.arc(startPos.x, startPos.y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    previewImageDataRef.current = null;
    emitMergedAnswer();
  };

  const finishPolygon = () => {
    if (polygonPoints.length < 3) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#ff1744";
    ctx.lineWidth = 2;
    drawPolygon(ctx, polygonPoints, null, true);
    setPolygonPoints([]);
    setIsDrawingPolygon(false);
    setCurrentMousePos(null);
    previewImageDataRef.current = null;
    emitMergedAnswer();
  };

  const cancelPolygon = () => {
    setPolygonPoints([]);
    setIsDrawingPolygon(false);
    setCurrentMousePos(null);
    restorePreviewBase();
    previewImageDataRef.current = null;
  };

  const clearCanvas = () => {
    restoreCanvas("");
    onChange("");
  };

  const toggleDrawMode = () => {
    setDrawMode((prev) => !prev);
    setIsDrawing(false);
    previewImageDataRef.current = null;
    if (isDrawingPolygon) {
      cancelPolygon();
    }
  };

  useEffect(() => {
    const img = imageRef.current;
    const container = containerRef.current;
    if (!img || !container) return;

    const onResize = () => setCanvasSizeFromImage();
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [imageUrl]);

  useEffect(() => {
    // Intentionally no auto-restore from merged value during drawing.
    // Merged value includes background image and can cause visual distortion
    // when used as live overlay source while user is drawing.
  }, [value]);

  if (!imageUrl) {
    return (
      <Box sx={{ p: 2, borderRadius: 2, border: "1px dashed rgba(255,255,255,0.35)" }}>
        <Typography>No image found for this question.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          border: "1px solid rgba(104,112,250,0.55)",
          backgroundColor: "rgba(18, 24, 40, 0.85)",
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={toggleDrawMode}
            disabled={disabled}
            startIcon={drawMode ? <CancelIcon /> : <EditIcon />}
            sx={{
              fontWeight: 700,
              px: 2.5,
              py: 1,
              borderWidth: 2,
              color: "#fff",
              backgroundColor: drawMode ? "#f44336" : "#2e7d32",
              "&:hover": {
                backgroundColor: drawMode ? "#d32f2f" : "#1b5e20",
              },
            }}
          >
            {drawMode ? "Çizim Modunu Kapat" : "Alan Çiz"}
          </Button>

          {drawMode && (
            <ToggleButtonGroup
              value={shapeType}
              exclusive
              onChange={(_e, newShape) => newShape && setShapeType(newShape)}
              size="small"
            >
              <ToggleButton value="rectangle">
                <CropSquareIcon sx={{ mr: 1 }} />
                Dikdörtgen
              </ToggleButton>
              <ToggleButton value="circle">
                <RadioButtonUncheckedIcon sx={{ mr: 1 }} />
                Daire
              </ToggleButton>
              <ToggleButton value="polygon">
                <PolylineIcon sx={{ mr: 1 }} />
                Poligon
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {drawMode && isDrawingPolygon && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="contained" size="small" onClick={finishPolygon} disabled={polygonPoints.length < 3}>
                Poligonu Tamamla ({polygonPoints.length} nokta)
              </Button>
              <Button variant="outlined" size="small" color="error" onClick={cancelPolygon}>
                İptal
              </Button>
            </Box>
          )}
        </Box>

        <Button size="small" color="error" variant="outlined" onClick={clearCanvas} disabled={disabled}>
          Temizle
        </Button>
      </Box>

      <Alert severity={drawMode ? "info" : "success"} sx={{ mb: 2 }}>
        {drawMode
          ? shapeType === "polygon"
            ? `Poligon için noktaları tıklayın. İlk noktaya dönerek veya "Poligonu Tamamla" ile bitirin. ${isDrawingPolygon ? `(${polygonPoints.length} nokta)` : ""}`
            : `Mouse ile ${shapeType === "rectangle" ? "dikdörtgen" : "daire"} çizin.`
          : "Çizime başlamak için hemen yukarıdaki 'Alan Çiz' butonuna basın."}
      </Alert>

      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          border: drawMode ? "2px solid #4cceac" : "2px solid rgba(104, 112, 250, 0.6)",
          backgroundColor: "#101015",
        }}
      >
        <img
          ref={imageRef}
          src={imageUrl}
          alt="question base"
          onLoad={setCanvasSizeFromImage}
          style={{ width: "100%", height: "auto", display: "block" }}
        />
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => {
            handleMouseUp();
            setCurrentMousePos(null);
          }}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
            touchAction: "none",
            cursor: drawMode ? "crosshair" : "default",
          }}
        />
      </Box>
    </Box>
  );
}
